import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { SPEEDS, readSpeed, saveSpeed, fmtSpeed } from "@/lib/videoSpeed";

// Délai avant fermeture du menu quand le « click » synthétisé n'arrive jamais après un appui au doigt.
export const TOUCH_CLOSE_MS = 250;

// Bouton « 1× » → petit menu 1× · 1,25× · 1,5× · 2×. Applique video.playbackRate (ré-appliqué à chaque
// nouvelle source : loadedmetadata / play) et mémorise le choix pour toutes les vidéos suivantes.
// Tactile : la vitesse est appliquée dès le relâché du doigt (pointerup) — le « click » n'est plus indispensable ;
// le menu reste monté jusqu'au click (ou TOUCH_CLOSE_MS) pour que ce click ne tombe pas sur ce qu'il y a dessous.
export const PlaybackSpeedControl = ({ videoRef, testId, className = "" }) => {
  const { t, lang } = useApp();
  const [speed, setSpeed] = useState(readSpeed);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);
  // Menu en portail (position fixe) : la boîte vidéo est en overflow-hidden et couperait la liste.
  const [pos, setPos] = useState(null);
  const place = () => {
    const r = rootRef.current?.getBoundingClientRect();
    if (!r) return;
    const below = r.bottom + 8 + 210 <= window.innerHeight;
    setPos(below ? { top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) } : { bottom: window.innerHeight - r.top + 8, right: Math.max(8, window.innerWidth - r.right) });
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const apply = () => { try { if (v.playbackRate !== speed) v.playbackRate = speed; } catch { /* noop */ } };
    apply();
    v.addEventListener("loadedmetadata", apply);
    v.addEventListener("play", apply);
    return () => { v.removeEventListener("loadedmetadata", apply); v.removeEventListener("play", apply); };
  }, [speed, videoRef]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (rootRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", close, true);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("pointerdown", close, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const closeMenu = () => { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; setOpen(false); };
  const choose = (v) => { setSpeed(v); saveSpeed(v); };
  // Doigt / stylet : vitesse appliquée immédiatement, fermeture au click qui suit (ou après TOUCH_CLOSE_MS).
  const onOptionPointerUp = (e, v) => {
    e.stopPropagation();
    if (e.pointerType === "mouse") return;
    choose(v);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(closeMenu, TOUCH_CLOSE_MS);
  };
  const onOptionClick = (e, v) => { e.stopPropagation(); choose(v); closeMenu(); };
  const toggle = (e) => { e.stopPropagation(); if (!open) place(); setOpen((o) => !o); };
  const stop = (e) => e.stopPropagation();

  return (
    <div ref={rootRef} className={`relative ${className}`} onPointerDown={stop} onPointerUp={stop}>
      <button type="button" onClick={toggle}
        className="h-9 min-w-9 px-2.5 rounded-full grid place-items-center text-white text-xs font-bold tabular-nums transition-transform hover:scale-105"
        style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", touchAction: "manipulation" }}
        aria-label={t("video.speed", "Vitesse de lecture")} aria-expanded={open} aria-haspopup="menu"
        data-testid={`${testId}-speed`} data-speed={speed}>
        {fmtSpeed(speed, lang)}
      </button>
      {open && pos && createPortal(
        <div ref={menuRef} role="menu" className="fixed z-[1200] min-w-[200px] rounded-xl overflow-hidden py-1 shadow-xl select-none"
          style={{ ...pos, background: "rgba(20,20,20,0.94)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.12)", touchAction: "manipulation" }}
          onPointerDown={stop} onPointerUp={stop} onClick={stop}
          data-testid={`${testId}-speed-menu`}>
          <div className="px-4 py-1.5 text-[11px] uppercase tracking-wide" style={{ color: "rgba(255,255,255,0.55)" }}>{t("video.speed", "Vitesse de lecture")}</div>
          {SPEEDS.map((v) => (
            <button key={v} type="button" role="menuitemradio" aria-checked={v === speed}
              onPointerUp={(e) => onOptionPointerUp(e, v)} onClick={(e) => onOptionClick(e, v)}
              className="w-full min-h-[44px] flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-white hover:bg-white/10 active:bg-white/15 transition-colors"
              data-testid={`${testId}-speed-${String(v).replace(".", "_")}`}>
              <span className="tabular-nums">{fmtSpeed(v, lang)}{v === 1 ? ` · ${t("video.speed_normal", "Normale")}` : ""}</span>
              {v === speed && <Check size={15} />}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default PlaybackSpeedControl;
